import React, { useState, useEffect } from 'react'
import { useHistory, useLocation, NavLink } from 'react-router-dom'
import PropTypes from 'prop-types'
import ReactSVG from 'react-svg'
import { useMutation } from '@apollo/react-hooks'
import classnames from 'classnames'

import REMOVE_FROM_WATCHLIST_MUTATION from '../../../../graphql/mutations/remove-from-watchlist.gql'
import removeIcon from '../../../../images/delete-default.svg'
import arrowLeft from '../../../../images/arrow-left-light.svg'
import editIcon from '../../../../images/pencil.svg'
import shareIcon from '../../../../images/share.svg'
import Imgix from '../../../components/imgix'
import Button from '../../../components/button'
import TileRentalBadge from '../../../components/shared/rental-badge'
import { getModalLocation } from '../../../lib/modal'
import { useScreenWidth } from '../../../hooks/use-screen-width'
import { useIsKid } from '../../../hooks/use-is-kid'
import { MODALS, STATIC_SCREENS, SCREEN_SIZE_BREAKPOINTS } from '../../../constants'
import { truncateListNameText, getTileMetadata, getDetailsUrl } from '../../../lib/utils'
import { segmentTrackClickContentCard } from '../../../segment/segment-track'
import { TILE_EXIT_MS, watchlistTypes } from '../constants'
import styles from './watchlistComponent.css'

function Watchlist({ watchlist }) {
  const {
    header,
    watchlistId,
    watchlistType,
    tiles
  } = watchlist

  const history = useHistory()
  const location = useLocation()
  const isKid = useIsKid()
  const [localTiles, setLocalTiles] = useState(tiles || [])
  const [deletingTileId, setDeletingTileId] = useState(null)
  const screenWidth = useScreenWidth()
  const [removeFromWatchlist] = useMutation(REMOVE_FROM_WATCHLIST_MUTATION)

  useEffect(() => {
    setLocalTiles(tiles || [])
  }, [tiles])

  const isMobileView = screenWidth <= SCREEN_SIZE_BREAKPOINTS.MIN_768
  const emptyList = !localTiles || localTiles.length === 0
  const tileCount = localTiles?.length || 0
  const tileCountText = `${tileCount} ${tileCount === 1 ? 'item' : 'items'}`
  const canEditAndDelete = watchlistType !== watchlistTypes.defaultList
  const canShare = localTiles && localTiles.length > 0 && !isKid

  const onTileDelete = async ({ contentItemId }) => {
    if (deletingTileId) {
      return
    }

    const previousTiles = localTiles
    setDeletingTileId(contentItemId)

    setTimeout(async () => {
      setLocalTiles((currentTiles) => (
        currentTiles.filter((tile) => tile.contentItem.id !== contentItemId)
      ))

      try {
        await removeFromWatchlist({
          variables: {
            watchlist: {
              id: watchlistId,
              listType: watchlistType
            },
            contentItemId
          }
        })
      } catch (error) {
        setLocalTiles(previousTiles)
      } finally {
        setDeletingTileId(null)
      }
    }, TILE_EXIT_MS)
  }

  const onConfirmDeleteList = () => {
    history.push(getModalLocation(
      location,
      MODALS.qsParams.confirmDeleteList,
      { listName: header, listId: watchlistId }
    ))
  }

  const onEditListName = () => {
    history.push(getModalLocation(
      location,
      MODALS.qsParams.renameNamedList,
      { listName: header, listId: watchlistId }
    ))
  }

  const onShareList = () => {
    history.push(getModalLocation(
      location,
      MODALS.qsParams.shareList,
      { listId: watchlistId, listType: watchlistType }
    ))
  }

  const EditListNameButton = () => {
    if (!canEditAndDelete) {
      return null
    }

    return (
      <button
        type="button"
        aria-label="Edit list name"
        onClick={onEditListName}
        className={styles.iconButtonFrame}
      >
        <div className={styles.iconButton}>
          <ReactSVG
            src={editIcon}
            beforeInjection={(svg) => {
              svg.setAttribute('class', styles.editIcon)
            }}
          />
        </div>
      </button>
    )
  }

  const DeleteListButton = () => {
    if (!canEditAndDelete) {
      return null
    }

    if (isMobileView) {
      return (
        <button
          type="button"
          aria-label="Delete list"
          onClick={onConfirmDeleteList}
          className={styles.iconButtonFrame}
        >
          <div className={styles.iconButton}>
            <ReactSVG
              src={removeIcon}
              beforeInjection={(svg) => {
                svg.setAttribute('class', styles.removeListIcon)
              }}
            />
          </div>
        </button>
      )
    }

    return (
      <Button
        text="Delete List"
        onClick={onConfirmDeleteList}
        darkBGSecondary
      />
    )
  }

  const ShareListButton = () => {
    if (!canShare) {
      return null
    }

    return (
      <Button
        wrapperClassName={styles.shareButton}
        onClick={onShareList}
      >
        <div className={styles.iconButton}>
          <ReactSVG
            src={shareIcon}
            beforeInjection={(svg) => {
              svg.setAttribute('class', styles.shareIcon)
            }}
          />
          Share List
        </div>
      </Button>
    )
  }

  const TileCountText = () => (
    <div className={styles.tileCount}>
      <span>
        {tileCountText}
      </span>
    </div>
  )

  return (
    <div className={styles.container}>
      <NavLink to={STATIC_SCREENS.MY_LISTS.screenId} className={styles.backLink}>
        <ReactSVG
          src={arrowLeft}
          beforeInjection={(svg) => {
            svg.setAttribute('class', styles.backLinkIcon)
          }}
        />
        Back to My Lists
      </NavLink>

      { isMobileView ? (
        <div className={styles.tileHeader}>
          <div className={styles.buttonGroup}>
            <span className={styles.title}>
              {truncateListNameText(header, 120, screenWidth)}
            </span>
            <div className={styles.buttonsRow}>
              <EditListNameButton />
              <DeleteListButton />
            </div>
          </div>

          <TileCountText />

          <div className={styles.buttonGroup}>
            <ShareListButton />
          </div>
        </div>
      ) : (
        <>
          <div className={styles.tileHeader}>
            <div className={styles.buttonGroup}>
              <span className={styles.title}>
                {truncateListNameText(header, 480, screenWidth)}
              </span>
              <div className={styles.buttonsRow}>
                <EditListNameButton />
              </div>
            </div>

            <div className={styles.buttonsRow}>
              <ShareListButton />
              <DeleteListButton />
            </div>
          </div>

          <TileCountText />
        </>
      ) }

      <div className={styles.tiles}>
        { emptyList ? (
          <div className={styles.emptyListContent}>
            <span className={classnames(styles.title, styles.emptyListTitle)}>
              There&apos;s nothing here yet!
            </span>

            <span className={styles.description}>
              When you add a TV show or movie to your list it will appear here
            </span>

            <NavLink to="/" className={styles.browseButton}>
              <Button
                text="Browse Neon"
              />
            </NavLink>
          </div>
        ) : (
          localTiles.map((tile, index) => {
            const metadata = getTileMetadata(tile.contentItem)
            const path = getDetailsUrl(tile.contentItem)
            const { isRental } = tile.contentItem

            return (
              <div
                className={deletingTileId === tile.contentItem.id ? `${styles.tile} ${styles.tileExit}` : styles.tile}
                key={tile.contentItem.id}
              >
                <div className={styles.content}>
                  <NavLink
                    to={path}
                    className={classnames(styles.imageWrapper, {
                      [styles.rentalTile]: isRental
                    })}
                    onClick={() => segmentTrackClickContentCard(
                      tile.contentItem,
                      index,
                      header,
                      location.pathname,
                      true
                    )}
                  >
                    <Imgix
                      className={styles.image}
                      src={tile.image}
                      recipe="series"
                    />

                    {isRental && (
                      <TileRentalBadge />
                    )}
                  </NavLink>

                  <div className={styles.listItemHeaderWrapper}>
                    <span>{tile.header}</span>
                    {metadata && (
                      <span className={styles.listItemMetadata}>
                        {metadata}
                      </span>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  className={styles.iconButtonFrame}
                  aria-label="Remove item from watchlist"
                  onClick={() => onTileDelete({ contentItemId: tile.contentItem.id })}
                >
                  <ReactSVG
                    src={removeIcon}
                    beforeInjection={(svg) => {
                      svg.setAttribute('class', styles.removeListItemIcon)
                    }}
                  />
                </button>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

Watchlist.propTypes = {
  watchlist: PropTypes.shape({
    __typename: PropTypes.string,
    id: PropTypes.string,
    header: PropTypes.string,
    watchlistId: PropTypes.string,
    watchlistType: PropTypes.string,
    tiles: PropTypes.arrayOf(
      PropTypes.shape({
        image: PropTypes.string,
        header: PropTypes.string,
        subHeader: PropTypes.string,
        contentItem: PropTypes.shape({
          id: PropTypes.string,
          __typename: PropTypes.string,
          path: PropTypes.string,
          isRental: PropTypes.bool
        })
      })
    )
  })
}

Watchlist.defaultProps = {
  watchlist: {}
}

export default Watchlist
